import React from "react";
import {
  NewsArticle,
  Review,
  WithContext,
  Book,
  Movie,
  Episode,
  MusicAlbum,
} from "schema-dts";
import { gql } from "utils/urql";
import Head from "next/head";
import JsonLd from "components/JsonLd";
import {
  ArticleJsonLdDataFragment,
  ArticleJsonLdAuthorFragment,
  ArticleJsonLdImageFragment,
  ArticleJsonLdReviewFragment,
  Maybe,
} from "types/api";

const isImage = (
  x: Maybe<ArticleJsonLdImageFragment>
): x is ArticleJsonLdImageFragment => !!x;

const toJsonLdReview = (
  review: ArticleJsonLdReviewFragment,
  {
    url,
    authors,
    dek,
    datePublished,
    shareImage1x1,
  }: Pick<
    ArticleJsonLdDataFragment,
    "url" | "authors" | "dek" | "datePublished" | "shareImage1x1"
  >
): WithContext<Review> | undefined => {
  let itemReviewed: Book | Movie | Episode | MusicAlbum | undefined;
  if (review.__typename === "BookReview" && review.isbn) {
    itemReviewed = {
      "@type": "Book",
      publisher: review.publisher
        ? {
            "@type": "Organization",
            name: review.publisher,
          }
        : undefined,
      author: {
        "@type": "Person",
        name: review.creator || undefined,
        sameAs: review.creatorUrl || undefined,
      },
      name: review.title,
      isbn: review.isbn,
      url: review.purchaseUrl || undefined,
    };
  } else if (review.__typename === "MovieReview" && review.url) {
    itemReviewed = {
      "@type": "Movie",
      director: review.creator
        ? {
            "@type": "Person",
            name: review.creator,
            sameAs: review.creatorUrl || undefined,
          }
        : undefined,
      name: review.title,
      sameAs: review.url,
      image: shareImage1x1?.url || undefined,
    };
  } else if (review.__typename === "MusicAlbumReview" && review.url) {
    itemReviewed = {
      "@type": "MusicAlbum",
      name: review.title,
      sameAs: review.url,
      byArtist: review.creator
        ? {
            "@type": "MusicGroup",
            name: review.creator,
            sameAs: review.creatorUrl || undefined,
          }
        : undefined,
    };
  } else if (review.__typename === "EpisodeReview" && review.season) {
    itemReviewed = {
      "@type": "Episode",
      name: review.title,
      sameAs: review.url || undefined,
      partOfSeason: {
        "@type": "CreativeWorkSeason",
        seasonNumber: review.season,
      },
      episodeNumber: review.episode,
    };
  }
  return (
    itemReviewed && {
      "@context": "https://schema.org",
      "@type": "Review",
      url,
      author: authors.map((author: ArticleJsonLdAuthorFragment) => ({
        "@type": "Person",
        name: author.displayName,
        sameAs: author.url,
      })),
      description: review.content || dek || undefined,
      datePublished: datePublished || undefined,
      publisher: {
        "@type": "Organization",
        name: "The Atlantic",
        sameAs: "https://www.theatlantic.com",
      },
      itemReviewed,
    }
  );
};

const ArticleJsonLd = ({
  title,
  seoTitle,
  url,
  dek,
  datePublished,
  dateModified,
  primaryChannel,
  authors,
  reviews,
  shareImage1x1,
  shareImage4x3,
  shareImage16x9,
  shareImageDefault,
  shareImageSquareDefault,
  shareImageLeadArt,
}: ArticleJsonLdDataFragment) => {
  const images = [
    shareImageLeadArt &&
      "image" in shareImageLeadArt &&
      shareImageLeadArt.image,
    shareImage1x1,
    shareImage4x3,
    shareImage16x9,
    shareImageDefault,
    shareImageSquareDefault,
  ].filter(isImage);
  return (
    <Head>
      {JsonLd<NewsArticle>({
        item: {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: seoTitle || title,
          alternativeHeadline: title,
          description: dek || undefined,
          url,
          datePublished: datePublished || undefined,
          dateModified: dateModified || undefined,
          publisher: {
            "@id": "https://www.theatlantic.com/#publisher",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": url,
          },
          ...(images &&
            images.length && {
              image: images.map(({ width, height, url }) => ({
                "@type": "ImageObject",
                width: {
                  "@type": "QuantitativeValue",
                  unitCode: "E37",
                  value: width,
                },
                height: {
                  "@type": "QuantitativeValue",
                  unitCode: "E37",
                  value: height,
                },
                url,
              })),
            }),
          ...(authors && {
            author: authors.map((author: ArticleJsonLdAuthorFragment) => ({
              "@type": "Person",
              name: author.displayName,
              sameAs: author.url,
            })),
          }),
          ...(primaryChannel && { articleSection: primaryChannel.displayName }),
        },
      })}

      {reviews &&
        reviews.map((review, index) => {
          const reviewItem = toJsonLdReview(review, {
            url,
            authors,
            dek,
            datePublished,
            shareImage1x1,
          });
          return (
            reviewItem && (
              <React.Fragment key={`ld+json-${index}`}>
                {JsonLd<Review>({ item: reviewItem })}
              </React.Fragment>
            )
          );
        })}
    </Head>
  );
};

ArticleJsonLd.fragment = gql`
  fragment articleJsonLdAuthor on Author {
    url
    displayName
  }

  fragment articleJsonLdImage on BasicImage {
    width
    height
    url
  }

  fragment articleJsonLdReview on Review {
    id
    title
    creator
    creatorUrl
    purchaseUrl
    image(width: 1080, height: 1080) {
      url
      width
      height
      srcSet
      captionText
      attributionText
      attributionUrl
      altText
    }
    content
    url
    ... on BookReview {
      publisher
      isbn
    }
    ... on EpisodeReview {
      season
      episode
    }
  }

  fragment articleJsonLdData on Article {
    url
    title
    seoTitle
    dek
    dateModified
    datePublished
    primaryChannel {
      displayName
    }
    authors {
      ...articleJsonLdAuthor
    }
    reviews {
      ...articleJsonLdReview
    }
    shareImage1x1: shareImage(width: 1080, height: 1080) {
      ...articleJsonLdImage
    }
    shareImage16x9: shareImage(width: 1600, height: 900) {
      ...articleJsonLdImage
    }
    shareImage4x3: shareImage(width: 1200, height: 900) {
      ...articleJsonLdImage
    }
    shareImageDefault: shareImage(width: 960, height: 540) {
      ...articleJsonLdImage
    }
    shareImageSquareDefault: shareImage(width: 540, height: 540) {
      ...articleJsonLdImage
    }
    # TODO: Create a swan-specific ArticleJsonLd component that uses the swan
    # leadArt dimensions
    shareImageLeadArt: leadArt {
      ... on LeadArtImage {
        image(width: 720, height: 405) {
          ...articleJsonLdImage
        }
      }
    }
  }
`;

export default ArticleJsonLd;
